import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminContactPage() {
  const [form, setForm] = useState({
    hero_subtitle: '',
    hero_title: '',
    phone_number: '',
    phone_description: '',
    email: '',
    email_description: '',
    india_title: '',
    india_address: '',
    uae_title: '',
    uae_address: '',
    map_directions_url: '',
    map_embed_url: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/contact-page`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const c = response.data.content || {};
        setForm((prev) => ({
          ...prev,
          hero_subtitle: c.hero_subtitle || '',
          hero_title: c.hero_title || '',
          phone_number: c.phone_number || '',
          phone_description: c.phone_description || '',
          email: c.email || '',
          email_description: c.email_description || '',
          india_title: c.india_title || '',
          india_address: c.india_address || '',
          uae_title: c.uae_title || '',
          uae_address: c.uae_address || '',
          map_directions_url: c.map_directions_url || '',
          map_embed_url: c.map_embed_url || '',
        }));
      } else {
        setError(response.data.message || 'Failed to fetch contact page content.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching contact page content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const token = getToken();
      const response = await axios.put(`${apiUrl}/api/admin/contact-page`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccess('Contact page updated successfully.');
        fetchContent();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Contact Page</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Hero Section</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Subtitle</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.hero_subtitle}
                        onChange={(e) => setField('hero_subtitle', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.hero_title}
                        onChange={(e) => setField('hero_title', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Phone Card</h5>
                  <span className="text-13 text-gray-400 d-block mt-4">Also used as the phone number shown in the hero corner.</span>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Phone Number</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.phone_number}
                        onChange={(e) => setField('phone_number', e.target.value)}
                        disabled={saving}
                        placeholder="+91 90486 48777"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Description</label>
                      <textarea
                        className="form-control py-11"
                        rows={2}
                        value={form.phone_description}
                        onChange={(e) => setField('phone_description', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Email Card</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Email</label>
                      <input
                        type="email"
                        className="form-control py-11"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        disabled={saving}
                        placeholder="enquiry@ifmedu.in"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Description</label>
                      <textarea
                        className="form-control py-11"
                        rows={2}
                        value={form.email_description}
                        onChange={(e) => setField('email_description', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Address Card</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">India — Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.india_title}
                        onChange={(e) => setField('india_title', e.target.value)}
                        disabled={saving}
                      />
                      <div className="mt-16">
                        <label className="h5 mb-8 fw-semibold font-heading">India — Address</label>
                        <textarea
                          className="form-control py-11"
                          rows={3}
                          value={form.india_address}
                          onChange={(e) => setField('india_address', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">UAE — Title</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.uae_title}
                        onChange={(e) => setField('uae_title', e.target.value)}
                        disabled={saving}
                      />
                      <div className="mt-16">
                        <label className="h5 mb-8 fw-semibold font-heading">UAE — Address</label>
                        <textarea
                          className="form-control py-11"
                          rows={3}
                          value={form.uae_address}
                          onChange={(e) => setField('uae_address', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading">"Get Direction" Link</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={form.map_directions_url}
                        onChange={(e) => setField('map_directions_url', e.target.value)}
                        disabled={saving}
                        placeholder="https://www.google.com/maps/place/..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Google Map Embed</h5>
                  <span className="text-13 text-gray-400 d-block mt-4">
                    Paste the "src" URL from Google Maps' Share &gt; Embed a map option.
                  </span>
                </div>
                <div className="card-body">
                  <textarea
                    className="form-control py-11"
                    rows={3}
                    value={form.map_embed_url}
                    onChange={(e) => setField('map_embed_url', e.target.value)}
                    disabled={saving}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-8 mb-24">
                <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminContactPage;
